// frontend/src/components/staff/WeeklyHourSummaryFooter.js
//
// Workforce — SCHED-SLICE1-E weekly hour summary footer.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §3.6 +
// Scenario 10 + visual DoD §18.1 (last bullet — no labor-cost / pay-rate
// visible).
//
// Renders one row per staff member with at least one non-cancelled shift
// in the visible week:
//   - name + role chip
//   - "X / Y hrs" (or "X / -- hrs" when limit is null)
//   - horizontal progress bar in role-color
//   - >= 90% of limit -> amber warn tint + warn icon
//   - > 100% of limit -> red tint + filled-warn icon + 4px red left-accent
//
// Click on a row filters the calendar to that staff's shifts only (the
// outer component owns the filter state).

import React from "react";
import { formatRoleLabel, getRoleColor } from "./scheduleBuilderUtils";
import "./WeeklyHourSummaryFooter.css";

const formatLimitLabel = (hours, limit) => {
  if (limit === null || limit === undefined) {
    return `${hours.toFixed(1)} hrs (no limit)`;
  }
  return `${hours.toFixed(1)} / ${Number(limit).toFixed(0)} hrs`;
};

const WeeklyHourSummaryFooter = ({
  summary,
  filterStaffProfileId,
  onStaffSelect,
}) => {
  if (!Array.isArray(summary) || summary.length === 0) {
    return null;
  }

  return (
    <footer
      className="weekly-hour-summary-footer"
      data-testid="weekly-hour-summary-footer"
      aria-label="Weekly hours per staff"
    >
      <h3 className="weekly-hour-summary-footer__title">Weekly hours</h3>
      <ul className="weekly-hour-summary-footer__list">
        {summary.map((row) => {
          const color = getRoleColor(row.role);
          const pct = Math.max(
            0,
            Math.min(150, row.pctOfLimit !== null ? row.pctOfLimit : (row.hours / 40) * 100)
          );
          const fillColor =
            row.status === "over"
              ? "#C53030"
              : row.status === "approaching"
                ? "#D69E2E"
                : color.bg;
          const isSelected = filterStaffProfileId === row.staffProfileId;
          const ariaLabel = `${row.name}, ${formatRoleLabel(row.role)}, ${
            row.limit === null
              ? `${row.hours} hours, no limit set`
              : `${row.hours} of ${row.limit} hours, status ${row.status}`
          }`;
          return (
            <li
              key={row.staffProfileId}
              className={
                row.status === "over"
                  ? "weekly-hour-summary-footer__row weekly-hour-summary-footer__row--over"
                  : "weekly-hour-summary-footer__row"
              }
            >
              <button
                type="button"
                className={
                  isSelected
                    ? "weekly-hour-summary-footer__row-btn weekly-hour-summary-footer__row-btn--active"
                    : "weekly-hour-summary-footer__row-btn"
                }
                onClick={() =>
                  typeof onStaffSelect === "function" &&
                  onStaffSelect(isSelected ? null : row.staffProfileId)
                }
                aria-pressed={isSelected}
                aria-label={ariaLabel}
                data-testid={`weekly-hour-summary-row-${row.staffProfileId}`}
              >
                <div className="weekly-hour-summary-footer__row-name">
                  {row.name}
                  <span
                    className="weekly-hour-summary-footer__role-chip"
                    style={{ background: color.bg, color: color.text }}
                  >
                    {formatRoleLabel(row.role)}
                  </span>
                </div>
                <div className="weekly-hour-summary-footer__row-hours">
                  {formatLimitLabel(row.hours, row.limit)}
                  {row.status === "approaching" && (
                    <span
                      className="weekly-hour-summary-footer__row-icon weekly-hour-summary-footer__row-icon--warn"
                      role="img"
                      aria-label="Approaching limit"
                    >
                      !
                    </span>
                  )}
                  {row.status === "over" && (
                    <span
                      className="weekly-hour-summary-footer__row-icon weekly-hour-summary-footer__row-icon--over"
                      role="img"
                      aria-label="Over limit"
                    >
                      !!
                    </span>
                  )}
                </div>
                <div
                  className="weekly-hour-summary-footer__bar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(pct)}
                >
                  <span
                    className="weekly-hour-summary-footer__bar-fill"
                    style={{
                      width: `${Math.min(100, pct)}%`,
                      background: fillColor,
                    }}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </footer>
  );
};

export default WeeklyHourSummaryFooter;
